/**
 * Monta a definição do relatório de abordagem para o pdfmake (issue #256).
 *
 * É função pura: recebe os dados já carregados (e as imagens já em dataURL) e
 * devolve a docDefinition. Quem gera o Blob é o gerarPdfAbordagem.js.
 */

import { dataHora, dataCurta, nomeAgente, dinheiro, quantidadeDoItem } from './formatadores';

// ---------------------------------------------------------------- paleta
const TINTA = '#1c2b36'; // texto e barras institucionais
const TINTA_FRACA = '#6b7c88'; // rótulos de campo
const LINHA = '#d4dce2';
const FUNDO_SECAO = '#eef2f5';
const FUNDO_CAIXA = '#f6f8f9';
const FUNDO_DELETADO = '#fbeeee';
const DESTRUTIVA = '#d32f2f'; // --sig-destrutiva (cores-sobrias.css)
const NAT_POLICIAL = '#5f6b1f'; // oliva da tela, escurecido para impressão
const NAT_FISCAL = '#7a1f42'; // bordô da tela, escurecido para impressão

// Padrões do documento, decididos com o usuário: marca d'água da Operação Foco,
// rodapé de emissão em toda página e uma ocorrência por página.
const PADRAO = {
  marca: { texto: 'OPERAÇÃO FOCO' },
  paginaPorOcorrencia: true,
};

/**
 * Marca d'água como BACKGROUND, e não pelo `watermark` do pdfmake: o watermark
 * é desenhado POR CIMA do conteúdo e lava as fotos anexadas — inaceitável num
 * relatório que serve de prova. Como background, ela fica atrás de tudo.
 */
const marcaDaguaSVG = ({ texto, cor = TINTA, opacidade = 0.06, tamanho = 54 }) => {
  const escapado = String(texto)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;');
  return (
    `<svg width="595" height="842" xmlns="http://www.w3.org/2000/svg">` +
    `<text x="297" y="421" text-anchor="middle" transform="rotate(-45 297 421)" ` +
    `font-family="Helvetica" font-weight="bold" font-size="${tamanho}" ` +
    `fill="${cor}" fill-opacity="${opacidade}">${escapado}</text></svg>`
  );
};

// ---------------------------------------------------------------- blocos
// título de seção: barra clara com filete escuro embaixo
const secao = texto => ({
  table: { widths: ['*'], body: [[{ text: texto, style: 'secao' }]] },
  layout: {
    hLineWidth: (i, node) => (i === node.table.body.length ? 1 : 0),
    vLineWidth: () => 0,
    hLineColor: () => TINTA,
    paddingLeft: () => 6,
    paddingRight: () => 6,
    paddingTop: () => 4,
    paddingBottom: () => 3,
    fillColor: () => FUNDO_SECAO,
  },
  margin: [0, 14, 0, 6],
});

// campo rotulado: rótulo miúdo em cima, valor embaixo
const campo = (rotulo, valor, opcoes = {}) => ({
  stack: [
    { text: rotulo.toUpperCase(), style: 'rotulo' },
    {
      text: valor === null || valor === undefined || valor === '' ? '—' : String(valor),
      style: 'valor',
      color: opcoes.cor || TINTA,
    },
  ],
  margin: [0, 0, 0, 8],
});

// grade de campos em N colunas, completando a última linha
const grade = (campos, colunas = 3) => {
  const linhas = [];
  for (let i = 0; i < campos.length; i += colunas) {
    const linha = campos.slice(i, i + colunas);
    while (linha.length < colunas) linha.push({ text: '' });
    linhas.push(linha);
  }
  return { table: { widths: Array(colunas).fill('*'), body: linhas }, layout: 'noBorders' };
};

const celula = (texto, extra = {}) => ({
  text: texto === null || texto === undefined || texto === '' ? '—' : texto,
  style: 'td',
  ...extra,
});

const vazio = texto => ({ text: texto, style: 'vazio', margin: [0, 2, 0, 6] });

const marcaDeletado = { text: ' (deletado)', color: DESTRUTIVA, fontSize: 7.5, italics: true };

// nome + tarja "(deletado)" na primeira coluna da linha
const linhaDeletada = (linha, indiceDaColuna, valor) => {
  const nova = [...linha];
  nova[indiceDaColuna] = { text: [{ text: valor }, marcaDeletado], style: 'td' };
  nova.__deletado = true;
  return nova;
};

// tabela de listagem: cabeçalho escuro, filetes horizontais finos
const tabela = (cabecalho, linhas, widths) => ({
  table: {
    headerRows: 1,
    dontBreakRows: true,
    widths,
    body: [cabecalho.map(t => ({ text: t.toUpperCase(), style: 'th' })), ...linhas],
  },
  layout: {
    hLineWidth: () => 0.5,
    vLineWidth: () => 0,
    hLineColor: () => LINHA,
    paddingLeft: () => 6,
    paddingRight: () => 6,
    paddingTop: () => 4,
    paddingBottom: () => 4,
    fillColor: (i, node) => {
      if (i === 0) return TINTA;
      return node.table.body[i].__deletado ? FUNDO_DELETADO : null;
    },
  },
  margin: [0, 0, 0, 4],
});

/**
 * Tabela COM o título da seção dentro dela (headerRows: 2). Dois ganhos: o
 * título nunca fica órfão no pé da página, e se repete no topo da continuação.
 */
const tabelaSecao = (titulo, cabecalho, linhas, widths, estilo = 'secao') => {
  const colunas = cabecalho.length;
  const doc = tabela(cabecalho, linhas, widths);
  doc.table.body.unshift([
    { text: titulo, colSpan: colunas, style: estilo, margin: [0, 2, 0, 2] },
    ...Array(colunas - 1).fill({}),
  ]);
  doc.table.headerRows = 2;
  // linha 0 = título, linha 1 = cabeçalho escuro, demais = dados
  doc.layout.fillColor = (i, node) => {
    if (i === 0) return FUNDO_SECAO;
    if (i === 1) return TINTA;
    return node.table.body[i].__deletado ? FUNDO_DELETADO : null;
  };
  doc.layout.hLineWidth = (i, node) => (i === 0 || i === node.table.body.length ? 0 : 0.5);
  doc.margin = estilo === 'secao' ? [0, 14, 0, 4] : [0, 8, 0, 4];
  return doc;
};

// ---------------------------------------------------------------- seções
function secaoIdentificacao(a) {
  const campos = [
    campo('Posto / Gerência', a.lotacao && a.lotacao.gerencia && a.lotacao.gerencia.nome),
    campo('Ala', a.lotacao && a.lotacao.ala && a.lotacao.ala.nome),
    campo('Registrada por', nomeAgente(a.usuario)),
    campo('Início', dataHora(a.dt_h_inicio)),
    campo('Término', dataHora(a.dt_h_fim)),
    campo('Evasão', a.evasao ? 'Sim' : 'Não'),
    campo('Origem', `${a.cidade_origem.nome} / ${a.cidade_origem.estado.nome}`),
    campo('Destino', `${a.cidade_destino.nome} / ${a.cidade_destino.estado.nome}`),
    campo('Apoio da volante', a.apoiado_por_gerencia_id ? 'Sim' : 'Não'),
  ];
  if (!a.ativo && a.deletado_por) {
    campos.push(campo('Deletada por', nomeAgente(a.deletado_por), { cor: DESTRUTIVA }));
  }
  return [secao('Identificação da abordagem'), grade(campos, 3)];
}

function secaoAgentes(a) {
  const linhas = [];
  if (a.usuario) {
    linhas.push([
      celula(nomeAgente(a.usuario)),
      celula([a.usuario.nome, a.usuario.sobrenome].filter(Boolean).join(' ')),
      celula('Cadastrante'),
    ]);
  }
  (a.equipe_abordagems || [])
    .filter(e => e.ativo && e.usuario)
    .forEach(e =>
      linhas.push([
        celula(nomeAgente(e.usuario)),
        celula([e.usuario.nome, e.usuario.sobrenome].filter(Boolean).join(' ')),
        celula('Participante'),
      ])
    );
  return linhas.length
    ? [tabelaSecao('Agentes responsáveis', ['Identificação', 'Nome civil', 'Participação'], linhas, ['*', '*', 90])]
    : [secao('Agentes responsáveis'), vazio('Nenhum agente vinculado.')];
}

function secaoVeiculos(veiculos) {
  const linhas = veiculos.map(v => [
    celula(v.tipo_veiculo && v.tipo_veiculo.nome),
    celula(v.placa),
    celula(v.chassi),
  ]);
  return linhas.length
    ? [tabelaSecao('Veículos', ['Tipo', 'Placa', 'Chassi'], linhas, ['*', 110, 190])]
    : [secao('Veículos'), vazio('Nenhum veículo registrado.')];
}

function secaoAbordados(abordados) {
  const linhas = abordados.map(p => {
    const linha = [
      celula(p.nome),
      celula(p.cpf),
      celula(p.tipo),
      celula(p.condutor ? 'Sim' : 'Não'),
      celula(p.condutor ? `${p.cnh || '—'} · cat. ${p.cnh_categoria || '—'}` : '—'),
      celula(p.condutor && p.cnh_validade ? dataCurta(p.cnh_validade) : '—'),
    ];
    return p.ativo ? linha : linhaDeletada(linha, 0, p.nome);
  });
  return linhas.length
    ? [
        tabelaSecao(
          'Pessoas abordadas',
          ['Nome', 'CPF', 'Tipo', 'Condutor', 'CNH', 'Validade'],
          linhas,
          ['*', 80, 60, 48, 110, 52]
        ),
      ]
    : [secao('Pessoas abordadas'), vazio('Nenhuma pessoa abordada registrada.')];
}

function secaoEmpresas(empresas) {
  if (!empresas.length) {
    return [secao('Empresas e documentos fiscais'), vazio('Nenhuma empresa registrada.')];
  }
  return empresas.map((e, indice) => {
    const documentos = e.documento_fiscals || [];
    const linhas = documentos.map(d => {
      const linha = [
        celula(d.tipo),
        celula(d.numero),
        celula(d.valor !== null && d.valor !== undefined ? dinheiro(d.valor) : '—', { alignment: 'right' }),
        celula(d.icms !== null && d.icms !== undefined ? dinheiro(d.icms) : '—', { alignment: 'right' }),
        celula(d.chave, { fontSize: 7 }),
      ];
      return d.ativo ? linha : linhaDeletada(linha, 0, d.tipo);
    });
    return {
      // o cabeçalho da empresa não pode ficar órfão no pé da página: enquanto o
      // bloco couber numa página, ele viaja inteiro (com o título da seção).
      unbreakable: linhas.length <= 8,
      stack: [
        ...(indice === 0 ? [secao('Empresas e documentos fiscais')] : []),
        {
          table: {
            widths: ['*'],
            body: [[{ text: `${e.nome || 'Empresa sem nome'}   ·   CNPJ ${e.cnpj}`, style: 'subtitulo' }]],
          },
          layout: {
            hLineWidth: () => 0,
            vLineWidth: () => 0,
            fillColor: () => FUNDO_CAIXA,
            paddingLeft: () => 6,
            paddingTop: () => 4,
            paddingBottom: () => 4,
          },
          margin: [0, 4, 0, 4],
        },
        linhas.length
          ? tabela(['Documento', 'Número', 'Valor', 'ICMS', 'Chave de acesso'], linhas, [70, 70, 70, 62, '*'])
          : vazio('Sem documentos fiscais.'),
      ],
    };
  });
}

function blocoOcorrencia(oco, opcoes = {}) {
  const policial = Number(oco.razao_autuacao.natureza_ocorrencia_id) === 2;
  const cor = policial ? NAT_POLICIAL : NAT_FISCAL;

  const faixa = {
    table: {
      widths: ['*', 'auto'],
      body: [
        [
          { text: `OCORRÊNCIA Nº ${oco.id}`, style: 'faixa' },
          {
            text:
              `${policial ? 'POLICIAL' : 'FISCAL'}   ·   ${oco.autuado ? 'AUTUADO' : 'NÃO AUTUADO'}` +
              `${oco.ativo ? '' : '   ·   DELETADA'}`,
            style: 'faixa',
            alignment: 'right',
          },
        ],
      ],
    },
    layout: {
      hLineWidth: () => 0,
      vLineWidth: () => 0,
      fillColor: () => cor,
      paddingLeft: () => 8,
      paddingRight: () => 8,
      paddingTop: () => 5,
      paddingBottom: () => 5,
    },
    margin: [0, 12, 0, 0],
  };

  const cabecalho = {
    table: {
      widths: ['*'],
      body: [
        [
          {
            stack: [
              { text: oco.razao_autuacao.descricao, style: 'razao' },
              grade(
                [
                  campo('Registro de ocorrência (R.O.)', oco.ro || 'Não informado', {
                    cor: oco.ro ? TINTA : DESTRUTIVA,
                  }),
                  campo('Autuado', oco.autuado ? 'Sim' : 'Não'),
                  campo('Registrada em', dataHora(oco.createdAt)),
                ],
                3
              ),
            ],
          },
        ],
      ],
    },
    layout: {
      hLineWidth: (i, node) => (i === node.table.body.length ? 0.5 : 0),
      vLineWidth: () => 0,
      hLineColor: () => LINHA,
      fillColor: () => FUNDO_CAIXA,
      paddingLeft: () => 8,
      paddingRight: () => 8,
      paddingTop: () => 6,
      paddingBottom: () => 0,
    },
  };

  const autuados = oco.autuado_ocorrencia.map(a => {
    const linha = [celula(a.abordado.nome), celula(a.abordado.tipo), celula(a.abordado.cpf)];
    return a.ativo ? linha : linhaDeletada(linha, 0, a.abordado.nome);
  });

  const itens = oco.item_grupo_tipo_ocorrencia.map(it => {
    const linha = [
      celula(it.tipo_ocorrencium && it.tipo_ocorrencium.nome),
      celula(it.grupo && it.grupo.nome),
      celula(it.item && it.item.nome),
      celula(quantidadeDoItem(it), { alignment: 'right' }),
    ];
    return it.ativo ? linha : linhaDeletada(linha, 2, it.item && it.item.nome);
  });

  const agentes = oco.equipe_ocorrencia.map(a => [
    celula(nomeAgente(a.usuario)),
    celula([a.usuario.nome, a.usuario.sobrenome].filter(Boolean).join(' ')),
  ]);

  const dinamicas = oco.ocorrencia_dinamicas.map(d => ({
    table: {
      widths: ['*'],
      body: [
        [
          {
            stack: [
              {
                columns: [
                  {
                    text: d.ativo ? 'RELATO' : 'RELATO SUBSTITUÍDO',
                    style: 'rotulo',
                    color: d.ativo ? TINTA_FRACA : DESTRUTIVA,
                  },
                  { text: dataHora(d.createdAt), style: 'rotulo', alignment: 'right' },
                ],
              },
              { text: d.dinamica, style: 'corrido', color: d.ativo ? TINTA : '#8a6b6b' },
            ],
          },
        ],
      ],
    },
    layout: {
      hLineWidth: () => 0,
      vLineWidth: i => (i === 0 ? 2 : 0),
      vLineColor: () => (d.ativo ? '#c7d0d6' : '#e3c9c9'),
      fillColor: () => (d.ativo ? FUNDO_CAIXA : FUNDO_DELETADO),
      paddingLeft: () => 8,
      paddingRight: () => 8,
      paddingTop: () => 5,
      paddingBottom: () => 6,
    },
    margin: [0, 4, 0, 4],
  }));

  return [
    {
      // faixa + razão da autuação viajam juntas: faixa sozinha no pé da página é
      // o defeito clássico desse tipo de relatório.
      unbreakable: true,
      stack: [...(opcoes.tituloSecao ? [secao(opcoes.tituloSecao)] : []), faixa, cabecalho],
      pageBreak: opcoes.paginaPorOcorrencia ? 'before' : undefined,
    },
    autuados.length
      ? tabelaSecao('Pessoas autuadas', ['Nome', 'Tipo', 'CPF'], autuados, ['*', 90, 100], 'subsecao')
      : [{ text: 'Pessoas autuadas', style: 'subsecao' }, vazio('Nenhuma pessoa autuada.')],
    itens.length
      ? tabelaSecao(
          'Itens da autuação',
          ['Tipo', 'Grupo', 'Item', 'Quantidade'],
          itens,
          [80, 100, '*', 100],
          'subsecao'
        )
      : [{ text: 'Itens da autuação', style: 'subsecao' }, vazio('Nenhum item lançado.')],
    agentes.length
      ? tabelaSecao('Agentes da ocorrência', ['Identificação', 'Nome civil'], agentes, ['*', '*'], 'subsecao')
      : [{ text: 'Agentes da ocorrência', style: 'subsecao' }, vazio('Nenhum agente vinculado.')],
    { text: 'Dinâmica', style: 'subsecao' },
    ...(dinamicas.length ? dinamicas : [vazio('Sem descrição da dinâmica.')]),
  ];
}

/**
 * Anexos, um por página. Foto entra como imagem; anexo que já é PDF ganha só
 * uma FOLHA DE ROSTO aqui — pdfmake não importa PDF, quem encaixa as páginas
 * originais logo depois é o gerarPdfAbordagem.js.
 *
 * INVARIANTE de que a mescla depende: as folhas de rosto são as ÚLTIMAS páginas
 * do documento, uma por anexo PDF, na mesma ordem em que aparecem aqui.
 */
function secaoAnexos(anexos) {
  const fotos = anexos.filter(a => a.tipo !== 'pdf');
  const pdfs = anexos.filter(a => a.tipo === 'pdf');

  const paginasFoto = fotos.map((foto, i) => ({
    pageBreak: 'before',
    stack: [
      { text: `ANEXO ${i + 1}`, style: 'rotulo', margin: [0, 0, 0, 4] },
      { image: foto.dataURL, fit: [515, 600], alignment: 'center' },
      ...(foto.legenda
        ? [{ text: foto.legenda, style: 'legenda', alignment: 'center', margin: [0, 6, 0, 0] }]
        : []),
    ],
  }));

  const paginasPdf = pdfs.map((doc, i) => ({
    pageBreak: 'before',
    stack: [
      { text: `ANEXO ${fotos.length + i + 1}`, style: 'rotulo', margin: [0, 0, 0, 4] },
      {
        table: {
          widths: ['*'],
          body: [
            [
              {
                stack: [
                  { text: 'DOCUMENTO ANEXADO EM PDF', style: 'subtitulo', margin: [0, 0, 0, 6] },
                  { text: doc.nome || 'documento.pdf', style: 'valor', margin: [0, 0, 0, 2] },
                  ...(doc.legenda ? [{ text: doc.legenda, style: 'legenda' }] : []),
                  {
                    text: 'O documento original segue nas páginas seguintes, na íntegra.',
                    style: 'legenda',
                    margin: [0, 10, 0, 0],
                  },
                ],
              },
            ],
          ],
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: () => LINHA,
          vLineColor: () => LINHA,
          fillColor: () => FUNDO_CAIXA,
          paddingLeft: () => 12,
          paddingRight: () => 12,
          paddingTop: () => 12,
          paddingBottom: () => 12,
        },
      },
    ],
  }));

  return [...paginasFoto, ...paginasPdf];
}

// ---------------------------------------------------------------- documento
export default function montaDocumento(dados, entrada = {}) {
  const opcoes = { ...PADRAO, ...entrada };
  const { abordagem: a, veiculos, abordados, empresas, ocorrencias, anexos } = dados;
  const emitidoEm = opcoes.emitidoEm || '';
  const emitidoPor = opcoes.emitidoPor || '';

  const resumo = [
    campo('Ocorrências registradas', String(ocorrencias.length)),
    campo('Veículos', String(veiculos.length)),
    campo('Pessoas abordadas', String(abordados.filter(p => p.ativo).length)),
    campo('Empresas', String(empresas.length)),
  ];

  return {
    pageSize: 'A4',
    pageMargins: [40, 96, 40, 48],
    info: {
      title: `Relatório de Abordagem ${a.id}`,
      author: 'SIG — Operação Foco',
      subject: 'Relatório de abordagem',
    },

    background: opcoes.marca
      ? () => ({ svg: marcaDaguaSVG(opcoes.marca), width: 595, absolutePosition: { x: 0, y: 0 } })
      : undefined,

    header: pagina =>
      pagina === 1
        ? {
            stack: [
              opcoes.brasao
                ? { image: opcoes.brasao, width: 38, alignment: 'center', margin: [0, 18, 0, 4] }
                : { text: '', margin: [0, 30, 0, 0] },
              { text: 'GOVERNO DO ESTADO DO RIO DE JANEIRO', style: 'orgao' },
              // A Operação Foco passou a integrar o Gabinete de Segurança
              // Institucional (agosto/2026); não está mais sob a Casa Civil.
              { text: 'Gabinete de Segurança Institucional  ·  Operação Foco', style: 'orgaoFraco' },
            ],
            margin: [40, 0, 40, 0],
          }
        : {
            margin: [40, 60, 40, 0],
            columns: [
              { text: `Relatório de Abordagem nº ${a.id}`, style: 'cabecalhoCorrido' },
              { text: 'SIG · Operação Foco', style: 'cabecalhoCorrido', alignment: 'right' },
            ],
          },

    // Rodapé de emissão: identifica o documento, quem tirou e quando — e serve
    // de rastro se a folha for impressa e circular solta.
    footer: () => ({
      margin: [40, 8, 40, 0],
      stack: [
        { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5, lineColor: LINHA }] },
        {
          columns: [
            {
              // sem repetir o órgão, que já está no cabeçalho e na marca
              // d'água: a linha precisa de folga para nomes longos antes de
              // esbarrar na numeração, à direita.
              text: [
                { text: `Abordagem nº ${a.id}`, bold: true },
                `   ·   Emitido em ${emitidoEm} por ${emitidoPor}`,
              ],
              style: 'rodape',
            },
            // A numeração NÃO sai aqui: as páginas dos anexos PDF entram depois,
            // pela mescla, e qualquer número calculado agora nasce errado.
            // Quem numera o documento inteiro é o carimbo do gerarPdfAbordagem.
            { text: '', width: 70 },
          ],
          margin: [0, 4, 0, 0],
        },
      ],
    }),

    content: [
      { text: 'RELATÓRIO DE ABORDAGEM', style: 'titulo' },
      {
        table: {
          widths: ['*', 'auto'],
          body: [
            [
              { text: `ABORDAGEM Nº ${a.id}`, style: 'faixaTitulo' },
              {
                text: a.ativo ? 'REGISTRO ATIVO' : 'REGISTRO DELETADO',
                style: 'faixaTitulo',
                alignment: 'right',
              },
            ],
          ],
        },
        layout: {
          hLineWidth: () => 0,
          vLineWidth: () => 0,
          fillColor: () => (a.ativo ? TINTA : DESTRUTIVA),
          paddingLeft: () => 8,
          paddingRight: () => 8,
          paddingTop: () => 6,
          paddingBottom: () => 6,
        },
        margin: [0, 6, 0, 10],
      },
      grade(resumo, 4),
      ...secaoIdentificacao(a),
      ...secaoAgentes(a),
      ...secaoVeiculos(veiculos),
      ...secaoAbordados(abordados),
      ...secaoEmpresas(empresas),
      ...ocorrencias.map((o, i) =>
        blocoOcorrencia(o, {
          ...opcoes,
          // o título da seção entra colado na primeira ocorrência
          tituloSecao: i === 0 ? `Ocorrências (${ocorrencias.length})` : null,
        })
      ),
      ...secaoAnexos(anexos || []),
    ],

    styles: {
      titulo: {
        fontSize: 15,
        bold: true,
        alignment: 'center',
        color: TINTA,
        characterSpacing: 1.2,
        margin: [0, 4, 0, 0],
      },
      faixaTitulo: { fontSize: 10, bold: true, color: '#ffffff', characterSpacing: 0.6 },
      faixa: { fontSize: 10, bold: true, color: '#ffffff', characterSpacing: 0.6 },
      orgao: { fontSize: 8.5, bold: true, alignment: 'center', color: TINTA, characterSpacing: 0.5 },
      orgaoFraco: { fontSize: 8, alignment: 'center', color: TINTA_FRACA },
      cabecalhoCorrido: { fontSize: 7.5, color: TINTA_FRACA },
      secao: { fontSize: 9, bold: true, color: TINTA, characterSpacing: 0.8 },
      subsecao: { fontSize: 8, bold: true, color: TINTA_FRACA, characterSpacing: 0.6 },
      subtitulo: { fontSize: 9, bold: true, color: TINTA },
      rotulo: { fontSize: 6.5, color: TINTA_FRACA, characterSpacing: 0.5, bold: true },
      valor: { fontSize: 9.5 },
      th: { fontSize: 7, bold: true, color: '#ffffff', characterSpacing: 0.4 },
      td: { fontSize: 8.5, color: TINTA },
      razao: { fontSize: 10, bold: true, color: TINTA, margin: [0, 0, 0, 6] },
      corrido: { fontSize: 9, alignment: 'justify', lineHeight: 1.25, margin: [0, 3, 0, 0] },
      legenda: { fontSize: 8, italics: true, color: TINTA_FRACA },
      vazio: { fontSize: 8.5, italics: true, color: TINTA_FRACA },
      rodape: { fontSize: 7, color: TINTA_FRACA },
    },
    defaultStyle: { font: 'Roboto', fontSize: 9, color: TINTA },
  };
}
